/**
 * Notificações do painel instalado no celular.
 *
 * Regras honestas que a interface precisa comunicar:
 *  - No iPhone só existe push quando o painel está adicionado à Tela de Início
 *    (iOS 16.4 ou mais novo). Em aba comum do Safari o navegador não oferece.
 *  - Permissão negada não pode ser pedida de novo pelo site: a pessoa precisa
 *    liberar nos ajustes do aparelho.
 *  - Push é um empurrão. A lista de avisos do painel continua sendo a fonte de
 *    verdade, porque entrega não é garantida.
 */
import { get, post } from './api.js';

export type EstadoPush =
  | 'sem_suporte'
  | 'precisa_instalar_ios'
  | 'negado'
  | 'disponivel'
  | 'ativo';

export interface SituacaoPush {
  estado: EstadoPush;
  inscrito: boolean;
  endpoint: string | null;
  /** Explicação em português para mostrar na tela. */
  explicacao: string;
}

export function ehIOS(): boolean {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

export function estaInstalado(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as { standalone?: boolean }).standalone === true
  );
}

export function suportaPush(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export async function registrarServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  try {
    return await navigator.serviceWorker.register('/sw.js', { scope: '/' });
  } catch {
    return null;
  }
}

export async function situacaoPush(): Promise<SituacaoPush> {
  // A ordem importa. O teste decisivo é a existência da API de push, não o
  // fabricante: no iPhone instalado na Tela de Início ela existe; em aba comum
  // do Safari, não. Adivinhar o sistema antes disso dava falso positivo.
  if (!suportaPush()) {
    if (ehIOS()) {
      return {
        estado: 'precisa_instalar_ios',
        inscrito: false,
        endpoint: null,
        explicacao:
          'No iPhone e no iPad, os avisos só funcionam com o painel adicionado à Tela de Início. Toque em Compartilhar e depois em "Adicionar à Tela de Início".',
      };
    }
    return {
      estado: 'sem_suporte',
      inscrito: false,
      endpoint: null,
      explicacao:
        'Este navegador não oferece notificações. No Android use o Chrome; no iPhone, instale o painel na Tela de Início.',
    };
  }

  if (Notification.permission === 'denied') {
    return {
      estado: 'negado',
      inscrito: false,
      endpoint: null,
      explicacao:
        'As notificações foram bloqueadas para este endereço. Libere nos ajustes do navegador ou do aparelho e recarregue a página.',
    };
  }

  const registro = await navigator.serviceWorker.getRegistration();
  const inscricao = await registro?.pushManager.getSubscription();

  if (inscricao) {
    return {
      estado: 'ativo',
      inscrito: true,
      endpoint: inscricao.endpoint,
      explicacao: 'Este aparelho recebe os avisos do painel.',
    };
  }
  return {
    estado: 'disponivel',
    inscrito: false,
    endpoint: null,
    explicacao: 'Ative para ser avisado neste aparelho quando um cliente precisar de atendimento.',
  };
}

function base64ParaUint8Array(base64: string): Uint8Array {
  const preenchimento = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalizado = (base64 + preenchimento).replace(/-/g, '+').replace(/_/g, '/');
  const bruto = window.atob(normalizado);
  const saida = new Uint8Array(bruto.length);
  for (let i = 0; i < bruto.length; i++) saida[i] = bruto.charCodeAt(i);
  return saida;
}

export async function ativarPush(): Promise<SituacaoPush> {
  const registro = (await navigator.serviceWorker.getRegistration()) ?? (await registrarServiceWorker());
  if (!registro) throw new Error('Não foi possível preparar o aplicativo para receber avisos.');

  const permissao = await Notification.requestPermission();
  if (permissao !== 'granted') {
    return situacaoPush();
  }

  const { publicKey } = await get<{ publicKey: string }>('/api/push/public-key');
  const inscricao = await registro.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: base64ParaUint8Array(publicKey) as BufferSource,
  });

  const bruto = inscricao.toJSON() as { endpoint?: string; keys?: { p256dh: string; auth: string } };
  if (!bruto.endpoint || !bruto.keys) throw new Error('Inscrição de notificação incompleta.');

  await post('/api/push/subscribe', {
    endpoint: bruto.endpoint,
    keys: bruto.keys,
    label: navigator.userAgent.slice(0, 120),
  });

  return situacaoPush();
}

/**
 * O navegador acha que está inscrito, mas o servidor pode ter perdido a
 * inscrição (troca de chave, limpeza de inscrição com erro). Reenvia em
 * silêncio a cada abertura do painel — em 07/10/2026 nenhum aparelho estava
 * inscrito no servidor, e o aviso só chegava com o painel aberto.
 */
export async function garantirInscricaoNoServidor(): Promise<void> {
  const registro = await navigator.serviceWorker.getRegistration();
  const inscricao = await registro?.pushManager.getSubscription();
  if (!inscricao) return;
  const bruto = inscricao.toJSON() as { endpoint?: string; keys?: { p256dh: string; auth: string } };
  if (!bruto.endpoint || !bruto.keys) return;
  await post('/api/push/subscribe', {
    endpoint: bruto.endpoint,
    keys: bruto.keys,
    label: navigator.userAgent.slice(0, 120),
  });
}

export async function desativarPush(): Promise<SituacaoPush> {
  const registro = await navigator.serviceWorker.getRegistration();
  const inscricao = await registro?.pushManager.getSubscription();
  if (inscricao) {
    await post('/api/push/unsubscribe', { endpoint: inscricao.endpoint }).catch(() => undefined);
    await inscricao.unsubscribe();
  }
  return situacaoPush();
}

export async function enviarTeste(): Promise<{ attempted: number; accepted: number }> {
  return post('/api/push/test', {});
}
